import { useEffect, useRef, useCallback, useState } from 'react'
import { getAccessToken } from '@/shared/api/client'

type MessageHandler = (data: unknown) => void

interface UseWebSocketOptions {
  /** Channels to subscribe to, e.g. ['queue:userId', 'notifications:userId'] */
  channels?: string[]
  /** Event handlers keyed by event type */
  onEvent?: Record<string, MessageHandler>
  /** Whether to connect (defaults to true when token is available) */
  enabled?: boolean
}

const WS_URL = import.meta.env.VITE_WS_URL || 'ws://localhost:8000/ws'
const MAX_RECONNECT_DELAY = 30000

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { channels = [], enabled = true } = options
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectAttempt = useRef(0)
  const reconnectTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const [connected, setConnected] = useState(false)
  const disposed = useRef(false)

  // Use refs for callbacks and channels to avoid reconnecting when handlers change
  const handlersRef = useRef(options.onEvent ?? {})
  handlersRef.current = options.onEvent ?? {}
  const channelsRef = useRef(channels)
  channelsRef.current = channels

  const connect = useCallback(() => {
    const token = getAccessToken()
    if (!token || !enabled || disposed.current) return

    const ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`)
    wsRef.current = ws

    ws.onopen = () => {
      if (disposed.current) { ws.close(); return }
      setConnected(true)
      reconnectAttempt.current = 0

      // Subscribe to channels
      for (const channel of channelsRef.current) {
        ws.send(JSON.stringify({ type: 'subscribe', channel }))
      }
    }

    ws.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data)
        // Server sends { type: 'event', event: 'notification.new', data: ... }
        const key = message.type === 'event' ? message.event : message.type
        const handler = key ? handlersRef.current[key] : undefined
        if (handler) handler(message.data ?? message)
      } catch {
        // ignore malformed messages
      }
    }

    ws.onclose = () => {
      setConnected(false)
      wsRef.current = null

      // Do NOT reconnect if the hook has been cleaned up
      if (disposed.current) return

      // Auto-reconnect with exponential backoff
      if (enabled) {
        const delay = Math.min(
          1000 * Math.pow(2, reconnectAttempt.current),
          MAX_RECONNECT_DELAY,
        )
        reconnectAttempt.current++
        reconnectTimer.current = setTimeout(connect, delay)
      }
    }

    ws.onerror = () => {
      ws.close()
    }
  }, [enabled])

  useEffect(() => {
    disposed.current = false
    connect()
    return () => {
      disposed.current = true
      clearTimeout(reconnectTimer.current)
      wsRef.current?.close()
    }
  }, [connect])

  const send = useCallback((data: unknown) => {
    wsRef.current?.send(JSON.stringify(data))
  }, [])

  return { connected, send }
}
